import { useState } from 'react';
import type { Media } from '@wineryxplorer/shared';

export interface MediaControlsProps {
  selectedMedia: Media[];
  onPublish?: (mediaIds: string[]) => void;
  onUnpublish?: (mediaIds: string[]) => void;
  onSetAsHero?: (mediaId: string) => void;
  onAddToGallery?: (mediaIds: string[]) => void;
  onRemoveFromGallery?: (mediaIds: string[]) => void;
  onDelete?: (mediaIds: string[]) => void;
  onEditTags?: (media: Media) => void;
  isLoading?: boolean;
  canSetHero?: boolean;
  canManageGallery?: boolean;
  className?: string;
}

export function MediaControls({
  selectedMedia,
  onPublish,
  onUnpublish,
  onSetAsHero,
  onAddToGallery,
  onRemoveFromGallery,
  onDelete,
  onEditTags,
  isLoading = false,
  canSetHero = true,
  canManageGallery = true,
  className = '',
}: MediaControlsProps) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const selectedIds = selectedMedia.map(m => m.id);
  const hasSelection = selectedMedia.length > 0;
  const isSingleSelection = selectedMedia.length === 1;
  const selectedPublished = selectedMedia.filter(m => m.isPublished);
  const selectedUnpublished = selectedMedia.filter(m => !m.isPublished);

  const handlePublish = () => {
    if (onPublish && selectedUnpublished.length > 0) {
      onPublish(selectedUnpublished.map(m => m.id));
    }
  };

  const handleUnpublish = () => {
    if (onUnpublish && selectedPublished.length > 0) {
      onUnpublish(selectedPublished.map(m => m.id));
    }
  };

  const handleSetAsHero = () => {
    if (onSetAsHero && isSingleSelection) {
      onSetAsHero(selectedMedia[0].id);
    }
  };

  const handleAddToGallery = () => {
    if (onAddToGallery && hasSelection) {
      onAddToGallery(selectedIds);
    }
  };

  const handleRemoveFromGallery = () => {
    if (onRemoveFromGallery && hasSelection) {
      onRemoveFromGallery(selectedIds);
    }
  };

  const handleDelete = () => {
    if (onDelete && hasSelection) {
      onDelete(selectedIds);
      setShowDeleteConfirm(false);
    }
  };

  const handleEditTags = () => {
    if (onEditTags && isSingleSelection) {
      onEditTags(selectedMedia[0]);
    }
  };

  if (!hasSelection) {
    return (
      <div className={`bg-gray-50 border border-gray-200 rounded-lg p-4 text-center ${className}`}>
        <p className="text-sm text-gray-500">
          Select media to see available actions
        </p>
      </div>
    );
  }

  return (
    <div className={`bg-gray-50 border border-gray-200 rounded-lg p-4 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-900">
          {selectedMedia.length} item{selectedMedia.length > 1 ? 's' : ''} selected
        </h3>
        <span className="text-xs text-gray-500">
          {selectedPublished.length} published, {selectedUnpublished.length} unpublished
        </span>
      </div>

      <div className="space-y-3">
        {/* Publication Controls */}
        <div className="flex flex-wrap gap-2">
          {selectedUnpublished.length > 0 && (
            <button
              onClick={handlePublish}
              disabled={isLoading}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              Publish ({selectedUnpublished.length})
            </button>
          )}

          {selectedPublished.length > 0 && (
            <button
              onClick={handleUnpublish}
              disabled={isLoading}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-info-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
              </svg>
              Unpublish ({selectedPublished.length})
            </button>
          )}
        </div>

        {/* Venue-specific Controls */}
        {canSetHero && isSingleSelection && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleSetAsHero}
              disabled={isLoading}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-info-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
              Set as Hero Image
            </button>
          </div>
        )}

        {canManageGallery && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleAddToGallery}
              disabled={isLoading}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-info-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add to Gallery
            </button>

            <button
              onClick={handleRemoveFromGallery}
              disabled={isLoading}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-info-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Remove from Gallery
            </button>
          </div>
        )}

        {/* Tag Management */}
        {isSingleSelection && (
          <div className="flex flex-wrap gap-2">
            <button
              onClick={handleEditTags}
              disabled={isLoading}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-info-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              Edit Tags
            </button>
          </div>
        )}

        {/* Delete */}
        <div className="pt-2 border-t border-gray-200">
          {!showDeleteConfirm ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isLoading}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-700 bg-gray-50 border border-red-300 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-info-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete Selected
            </button>
          ) : (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-red-700">
                Delete {selectedMedia.length} item{selectedMedia.length > 1 ? 's' : ''}?
              </span>
              <button
                onClick={handleDelete}
                disabled={isLoading}
                className="px-3 py-1 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50"
              >
                Confirm
              </button>
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isLoading}
                className="px-3 py-1 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}